/* GASSISA
=========================================================================================
* Descripción: Panel de búsqueda de artículos formato general
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 10/11/2024 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Limpieza de comando externo consumido
*      3       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Indicador visual de medida seleccionada
*      4       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Retiro de indicador anterior por borde
*      5       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Visual de medidas sin manejo o sin existencia
*      6       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Desplazamiento horizontal al navegar medidas
*      7       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Visualización de total al final de medidas
*      8       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Retorno visual al inicio de medidas
*      9       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Columnas fijas en panel de medidas
*      10      | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Scroll manual entre columnas fijas
*      11      | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Ajuste fino de retorno a primera medida
* --------------------------------------------------------------------------------------- 
*/
import React, { useState, useRef, forwardRef, useEffect, useImperativeHandle } from 'react';

const SizeSoftXMedidasExistencias = forwardRef(
  ({ tallas = [], onKeyDown, hadleEventKeySender, tallaSelect, onCommandConsumed }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const containerRef = useRef(null);
    const tallaRefs = useRef([]);

    const notifyTallaSelected = (index) => {
      if (tallaSelect && tallas[index]) {
        tallaSelect(tallas[index]);
      }
    };

    const handleKeyDown = (eventOrKey) => {
      const key = eventOrKey?.key ?? eventOrKey;
      if (!key) return;

      if (key === 'ArrowRight' && selectedIndex < tallas.length - 1) {
        const newIndex = selectedIndex + 1;
        setSelectedIndex(newIndex);
        notifyTallaSelected(newIndex);
      }

      if (key === 'ArrowLeft' && selectedIndex > 0) {
        const newIndex = selectedIndex - 1;
        setSelectedIndex(newIndex);
        notifyTallaSelected(newIndex);
      }

      if (key === 'Enter' && onKeyDown && tallas.length > 0 && tallas[selectedIndex]?.manejamedida !== false) {
        onKeyDown(tallas[selectedIndex]);
      }
    };

    const handleClick = (index) => {
      if (tallas[index]?.manejamedida === false) return;
      setSelectedIndex(index);
      notifyTallaSelected(index);
    };

    const handleDoubleClickTalla = (index) => {
      if (tallas[index]?.manejamedida === false) return;
      setSelectedIndex(index);
      notifyTallaSelected(index);
      if (onKeyDown && tallas[index]) {
        onKeyDown(tallas[index]);
      }
    };

    useEffect(() => {
      if (!hadleEventKeySender) return;

      const key = hadleEventKeySender.key ?? hadleEventKeySender;
      if (!key) return;

      handleKeyDown(key);
      onCommandConsumed?.();
    }, [hadleEventKeySender]);

    useEffect(() => {
      if (selectedIndex > tallas.length - 1) {
        setSelectedIndex(0);
      }
    }, [tallas, selectedIndex]);

    useEffect(() => {
      if (tallas.length > 0) {
        notifyTallaSelected(selectedIndex);
      }
    }, [selectedIndex, tallas]);

    useEffect(() => {
      const selectedCell = tallaRefs.current[selectedIndex];
      const scrollContainer = containerRef.current?.closest?.('.medidas-scroll-container');
      if (!selectedCell || !scrollContainer) return;

      if (selectedIndex === 0) {
        scrollContainer.scrollTo({
          left: 0,
          behavior: 'smooth',
        });
        return;
      }

      const stickyWidth = 70;
      const padding = 10;
      const cellLeft = selectedCell.offsetLeft;
      const cellRight = cellLeft + selectedCell.offsetWidth;
      const visibleLeft = scrollContainer.scrollLeft + stickyWidth;
      const visibleRight = scrollContainer.scrollLeft + scrollContainer.clientWidth - stickyWidth;

      if (cellLeft < visibleLeft) {
        scrollContainer.scrollTo({
          left: Math.max(0, cellLeft - stickyWidth - padding),
          behavior: 'smooth',
        });
      } else if (cellRight > visibleRight) {
        scrollContainer.scrollTo({
          left: cellRight - scrollContainer.clientWidth + stickyWidth + padding,
          behavior: 'smooth',
        });
      }
    }, [selectedIndex, tallas]);

    useImperativeHandle(ref, () => ({
      focus: () => {
        containerRef.current?.focus();
      }
    }));

    if (!tallas || tallas.length === 0) {
      return <div>No hay tallas disponibles</div>;
    }

    const cellWidth = 40;
    const leftSticky = {
      position: 'sticky',
      left: 0,
      zIndex: 3,
      boxShadow: '2px 0 4px rgba(0,0,0,.12)',
    };
    const rightSticky = {
      position: 'sticky',
      right: 0,
      zIndex: 3,
      boxShadow: '-2px 0 4px rgba(0,0,0,.12)',
    };
    const arrowCellStyle = {
      height: '14px',
      width: `${cellWidth}px`,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
    };
    
    return (
      <div
        className="tallasLista"
        style={{
          display: 'grid',
          gridTemplateColumns: `70px repeat(${tallas.length}, ${cellWidth}px) 70px`,
          gap: '2px',
          touchAction: 'manipulation',
        }}
        tabIndex={0}
        ref={containerRef}
        onKeyDown={handleKeyDown}
      >
        <div style={{ height: '14px', width: '70px', backgroundColor: 'white', ...leftSticky }} />
        {tallas.map((item, index) => (
          <div key={`indicador-${index}`} style={arrowCellStyle} aria-hidden="true">
            {index === selectedIndex && (
              <span
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '7px solid transparent',
                  borderRight: '7px solid transparent',
                  borderTop: '10px solid #1a237e',
                  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.25))',
                }}
              />
            )}
          </div>
        ))}
        <div style={{ height: '14px', width: '70px', backgroundColor: 'white', ...rightSticky }} />

        <div
          style={{ padding: '2px', backgroundColor: 'yellow', fontWeight: 'bold', width: '70px', ...leftSticky }}
        >
          Tallas
        </div>
        {tallas.map((item, index) => (
          <div
            key={index}
            ref={(element) => {
              tallaRefs.current[index] = element;
            }}
            style={{
              padding: '2px',
              fontWeight: 'bold',
              backgroundColor: 'yellow',
              textAlign: 'center',
              cursor: 'pointer',
              width: `${cellWidth}px`,
              touchAction: 'manipulation',
            }}
            onClick={() => handleClick(index)}
            onDoubleClick={() => handleDoubleClickTalla(index)}
          >
            {item.talla}
          </div>
        ))}
        <div
          style={{ backgroundColor: 'yellow', fontWeight: 'bold', width: '70px', ...rightSticky }}
        >
          Total
        </div>

        <div style={{ backgroundColor: 'lightgray', width: '70px', ...leftSticky }}>Cantidad</div>
        {tallas.map((item, index) => (
          <div
            key={index}
            style={{
              padding: '2px',
              backgroundColor: 'white',
              textAlign: 'center',
              cursor: item.manejamedida === false ? 'not-allowed' : 'pointer',
              width: `${cellWidth}px`,
              color: item.manejamedida === false ? '#b71c1c' : 'black',
              fontWeight: item.manejamedida === false ? 'bold' : 'normal',
              touchAction: 'manipulation',
            }}
            onClick={() => handleClick(index)}
            onDoubleClick={() => handleDoubleClickTalla(index)}
          >
            {item.manejamedida === false ? '🔴' : Number(item.cantidad) === 0 ? '👎' : item.cantidad}
          </div>
        ))}
        <div style={{ backgroundColor: 'lightgray', fontWeight: 'bold', width: '70px', ...rightSticky }}>
          {tallas.reduce((acc, item) => acc + (Number(item.cantidad) || 0), 0)}
        </div>
      </div>
    );
  }
);

export default SizeSoftXMedidasExistencias;
